import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import LayoutSelector from '../WidgetEditComponents/LayoutSelector';
import AchievementsManagement from '../WidgetEditComponents/AchievementsManagement';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function AchievementsBannerEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Layout-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.achievementLayout);
    
    // Image Upload States
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 AchievementsBanner initialized - layout variations now active');
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.achievementLayout;
        
        // ✅ CRITICAL: Only apply layout variations on ACTUAL layout changes, not widget switches
        if (!isInitialized) {
            console.log('🚫 AchievementsBanner: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 AchievementsBanner: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 AchievementsBanner REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS
        const layoutVariations = {
            'classic-horizontal': {
                containerSpacing: 80,
                showSeparator: true,
                showIcons: true,
                showFrames: false,
                framePadding: 0,
                frameBorderRadius: 0
            },
            'minimal-text-only': {
                containerSpacing: 60,
                showSeparator: false,
                showIcons: false,
                showFrames: false,
                framePadding: 0,
                frameBorderRadius: 0
            },
            'framed-cards': {
                containerSpacing: 40,
                showSeparator: false,
                showIcons: true,
                showFrames: true,
                framePadding: 24,
                frameBorderRadius: 12,
                frameBorderWidth: 1,
                frameShadowIntensity: 6,
                frameBlurIntensity: 10,
                frameBackgroundColor: 'rgba(255, 255, 255, 0.05)',
                frameBorderColor: '#e5e7eb',
                // ✅ MOBILE-OPTIMIERTE DEFAULTS
                mobileMaxWidth: 280,
                mobileFramePadding: 20,
                mobileIconSize: 32,
                titleMarginMobile: 30
            }
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ KRITISCH: BATCH UPDATE - Alle Werte auf einmal setzen
            const safePosition = widget.position !== undefined && widget.position !== null ? widget.position : 0;
            const updatedEditData = { ...editData, ...variations };
            
            // ✅ FIREBASE-SAFE: Position immer mitgesendet
            onUpdate({ ...updatedEditData, position: safePosition });
            
            console.log(`✅ AchievementsBanner layout variations applied via batch update`);
        }
    }, [editData.achievementLayout, isInitialized, previousLayoutType, editData, onUpdate, widget.position]);

    // Layout Change Handler
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log('🎨 AchievementsBanner layout change:', { layoutId, layoutConfig });
        
        const fakeEvent = {
            target: {
                name: 'achievementLayout',
                value: layoutId,
                type: 'text'
            }
        };
        handleInputChange(fakeEvent);
    };

    // 🚀 REVOLUTIONARY TEXT SETTINGS - Mit Show/Hide Toggles
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'Main Title',
            type: 'input',
            placeholder: 'Our Achievements',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        },
        // ✅ NEU: Highlight Words Feld hinzufügen
        {
            fieldName: 'titleHighlightWords',
            label: 'Title - Highlight Words',
            type: 'input',
            placeholder: 'Achievements, Success (comma separated)',
            description: 'Enter words to highlight with different styling',
            fontField: 'titleHighlightFont',
            fontType: 'small', // ✅ Anderer Font-Pool als Main Title
            effectField: 'titleHighlightEffect'
        },
        {
            fieldName: 'subtitleText',
            label: 'Subtitle',
            type: 'input',
            placeholder: 'What we have accomplished',
            fontField: 'subtitleFont',
            fontType: 'small',
            effectField: 'subtitleEffect'
        },
        {
            fieldName: 'descriptionText',
            label: 'Description',
            type: 'textarea',
            placeholder: 'Enter description text...',
            rows: 3,
            fontField: 'descriptionFont',
            fontType: 'small',
            effectField: 'descriptionEffect'
        }
    ];

    // 🎨 CLEAN COLOR CONFIGURATION
    const colorConfig = [
        [
            {
                fieldName: 'titleColor',
                label: 'Title',
                defaultValue: '#1f2937',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'subtitleColor',
                label: 'Subtitle',
                defaultValue: '#6b7280',
                icon: '/img/text.svg'
            },
            {
                fieldName: 'descriptionColor',
                label: 'Description',
                defaultValue: '#374151',
                icon: '/img/text.svg'
            }
        ],
        [
            {
                fieldName: 'backgroundColor',
                label: 'Background',
                defaultValue: '#ffffff',
                icon: '/img/background.svg'
            },
            {
                fieldName: 'numberColor',
                label: 'Numbers',
                defaultValue: '#1f2937',
                icon: '/img/text-fat.svg'
            },
            {
                fieldName: 'textColor',
                label: 'Text',
                defaultValue: '#6b7280',
                icon: '/img/text.svg'
            }
        ],
        [
            {
                fieldName: 'lineColor',
                label: 'Line Color',
                defaultValue: '#6b7280',
                icon: '/img/element.svg'
            },
            {
                fieldName: 'frameBorderColor',
                label: 'Frame Border',
                defaultValue: '#e5e7eb',
                icon: '/img/border.svg'
            },
            {
                fieldName: 'frameBackgroundColor',
                label: 'Frame Background',
                defaultValue: 'rgba(255, 255, 255, 0.05)',
                icon: '/img/background.svg'
            }
        ]
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION
    // 🚀 CUSTOM LAYOUT PROPERTIES - Keine widget-spezifischen Layout Properties
    const customLayoutProperties = [];

    const advancedConfig = [
        // 📐 Widget-spezifische Dimensions - KORRIGIERTE FIELD NAMES
        {
            fieldName: 'iconSize',
            label: 'Icon Size',
            type: 'slider',
            defaultValue: 40,
            min: 24,
            max: 80,
            step: 4,
            unit: 'px',
            color: '#10b981',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'containerSpacing',
            label: 'Achievement Spacing',
            type: 'slider',
            defaultValue: 80,
            min: 20,
            max: 120,
            step: 10,
            unit: 'px',
            color: '#f59e0b',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'containerSpacingMobile',
            label: 'Mobile Spacing',
            type: 'slider',
            defaultValue: 30,
            min: 15,
            max: 60,
            step: 5,
            unit: 'px',
            color: '#ef4444',
            icon: '/img/element.svg'
        },
        
        // 🎨 Display - KORRIGIERTE FIELD NAMES
        {
            fieldName: 'lineWidth',
            label: 'Line Width',
            type: 'slider',
            defaultValue: 2,
            min: 1,
            max: 8,
            step: 1,
            unit: 'px',
            color: '#6b7280',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'lineHeight',
            label: 'Line Height',
            type: 'slider',
            defaultValue: 60,
            min: 5,
            max: 100,
            step: 5,
            unit: 'px',
            color: '#374151',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'backgroundImageOpacity',
            label: 'Background Opacity',
            type: 'slider',
            defaultValue: 0.1,
            min: 0.05,
            max: 1,
            step: 0.05,
            unit: '',
            color: '#9ca3af',
            icon: '/img/background.svg'
        },
        
        // 🎨 Separator Styles - Holistische Trenner
        {
            fieldName: 'separatorStyle',
            label: 'Separator Style',
            type: 'select',
            options: [
                { value: 'line', label: 'Simple Line' },
                { value: 'gradient-line', label: 'Gradient Line' },
                { value: 'dotted-line', label: 'Dotted Line' },
                { value: 'dashed-line', label: 'Dashed Line' },
                { value: 'double-line', label: 'Double Line' },
                { value: 'wave-line', label: 'Wave Line' },
                { value: 'zigzag-line', label: 'Zigzag Line' },
                { value: 'diamond-pattern', label: 'Diamond Pattern' },
                { value: 'circle-dots', label: 'Circle Dots' },
                { value: 'spiritual-ornament', label: 'Spiritual Ornament' },
                { value: 'none', label: 'No Separator' }
            ],
            defaultValue: 'line',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'separatorSpacing',
            label: 'Separator Spacing',
            type: 'slider',
            defaultValue: 20,
            min: 0,
            max: 60,
            step: 5,
            unit: 'px',
            color: '#8b5cf6',
            icon: '/img/element.svg'
        },
        
        // ⚙️ Behavior - KORRIGIERTE FIELD NAMES
        {
            fieldName: 'showTitleText',
            label: 'Show Title',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text-fat.svg'
        },
        {
            fieldName: 'showSeparator',
            label: 'Show Separator',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/element.svg'
        },
        {
            fieldName: 'showIcons',
            label: 'Show Icons',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/element.svg'
        },
        {
            fieldName: 'showFrames',
            label: 'Show Frames',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'framePadding',
            label: 'Frame Padding',
            type: 'slider',
            defaultValue: 24,
            min: 8,
            max: 40,
            step: 4,
            unit: 'px',
            color: '#10b981',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'frameBorderRadius',
            label: 'Frame Radius',
            type: 'slider',
            defaultValue: 12,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            color: '#3b82f6',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'frameBorderWidth',
            label: 'Frame Border Width',
            type: 'slider',
            defaultValue: 1,
            min: 0,
            max: 4,
            step: 1,
            unit: 'px',
            color: '#8b5cf6',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'frameShadowIntensity',
            label: 'Frame Shadow',
            type: 'slider',
            defaultValue: 6,
            min: 0,
            max: 20,
            step: 2,
            unit: 'px',
            color: '#6b7280',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'frameBlurIntensity',
            label: 'Frame Blur',
            type: 'slider',
            defaultValue: 10,
            min: 0,
            max: 30,
            step: 2,
            unit: 'px',
            color: '#9ca3af',
            icon: '/img/element.svg'
        },
        
        // 📱 MOBILE-SPECIFIC DESIGN SETTINGS
        {
            fieldName: 'mobileMaxWidth',
            label: 'Mobile Card Width',
            type: 'slider',
            defaultValue: 280,
            min: 200,
            max: 400,
            step: 10,
            unit: 'px',
            color: '#ec4899',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'mobileFramePadding',
            label: 'Mobile Frame Padding',
            type: 'slider',
            defaultValue: 20,
            min: 8,
            max: 40,
            step: 4,
            unit: 'px',
            color: '#f97316',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'mobileIconSize',
            label: 'Mobile Icon Size',
            type: 'slider',
            defaultValue: 32,
            min: 20,
            max: 60,
            step: 4,
            unit: 'px',
            color: '#06b6d4',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'titleMarginMobile',
            label: 'Mobile Title Margin',
            type: 'slider',
            defaultValue: 30,
            min: 10,
            max: 60,
            step: 5,
            unit: 'px',
            color: '#84cc16',
            icon: '/img/element.svg'
        },
        
        // ✅ HIGHLIGHT SETTINGS - Nur spezielle Highlight-Properties
        {
            fieldName: 'titleHighlightColor',
            label: 'Title Highlight Color',
            type: 'color',
            defaultValue: '#D4A574',
            icon: '/img/text-fat.svg'
        },
        // ✅ NEU: Margin Controls für präzise Positionierung
        {
            fieldName: 'titleHighlightMarginLeft',
            label: 'Highlight Margin Left',
            type: 'slider',
            defaultValue: 0,
            min: -50,        // ✅ Erweitert: -50px für Überlappungen
            max: 50,         // ✅ Erweitert: +50px für große Abstände
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'titleHighlightMarginRight',
            label: 'Highlight Margin Right',
            type: 'slider',
            defaultValue: 0,
            min: -50,
            max: 50,
            step: 1,
            unit: 'px',
            icon: '/img/element.svg'
        }
    ];

    // 📝 FONT CONFIGURATION
    const fontConfig = [
        {
            label: 'Title Font',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            defaultSize: '36',
            defaultWeight: '700'
        },
        // ✅ NEU: Highlight Font Settings hinzufügen
        {
            label: 'Title - Highlight Words',
            sizeField: 'titleHighlightSize',
            weightField: 'titleHighlightWeight',
            defaultSize: '48', // Gleich wie Title
            defaultWeight: '800'
        },
        {
            label: 'Subtitle Font',
            sizeField: 'subtitleSize',
            weightField: 'subtitleWeight',
            defaultSize: '18',
            defaultWeight: '600'
        },
        {
            label: 'Description Font',
            sizeField: 'descriptionSize',
            weightField: 'descriptionWeight',
            defaultSize: '16',
            defaultWeight: '400'
        },
        {
            label: 'Achievement Number',
            sizeField: 'numberSize',
            weightField: 'numberWeight',
            defaultSize: '48',
            defaultWeight: '700'
        },
        {
            label: 'Achievement Text',
            sizeField: 'textSize',
            weightField: 'textWeight',
            defaultSize: '16',
            defaultWeight: '400'
        }
    ];

    return (
        <div>
            <div className="top-widget-edit-container">
            
                {/* 1. LAYOUT SELECTOR */}
                <LayoutSelector
                    widgetType="achievementsBanner"
                    selectedLayout={editData.achievementLayout || 'classic-horizontal'}
                    onLayoutChange={handleLayoutChange}
                />
                
                {/* 2. STYLES - Text Settings */}
                <TextSettingsSection 
                    textConfig={textConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={false}
                />

                {/* 3. BILD - Background Image Container */}
                <ImageContainer
                    label="Background Image"
                    fieldName="backgroundImage"
                    currentImage={editData.backgroundImage}
                    onUpload={uploadImage}
                    isUploading={isUploadingBackground}
                    setIsUploading={setIsUploadingBackground}
                    controls={[
                        {
                            type: 'size',
                            label: 'Image Opacity',
                            fieldName: 'backgroundImageOpacity',
                            defaultValue: 20,
                            min: 5,
                            max: 100,
                            step: 5,
                            unit: '%',
                            description: 'Opacity of the background image',
                            convertValue: true
                        },
                        {
                            type: 'image-effect',
                            label: 'Image Effect',
                            fieldName: 'imageEffect',
                            defaultValue: 'none'
                        }
                    ]}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    theme="default"
                />

                {/* 4. DER REST - Achievements Management */}
            <AchievementsManagement 
                editData={editData}
                handleInputChange={handleInputChange}
                />

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard Layout Properties */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                />

                {/* 5. Advanced Settings - Widget-spezifische Properties */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />

                {/* 6. More Settings - Mit Colors Dropdown */}
                <MoreSettingsSection 
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={[]} // Leer - jetzt in Advanced Settings
                    editData={editData}
                    handleInputChange={handleInputChange}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
                
            </div>
        </div>
    );
}

export default AchievementsBannerEdit;